import { useId } from "react";
import { Check, Info } from "lucide-react";
import { Icon } from '@iconify/react';
import { twMerge } from "tailwind-merge";
import "./AditionalServiceCard.css";

const AditionalServiceCard = ({
  price,
  title,
  description,
  icon,
  isSelected = false,
  isDesabled = false,
  onSelect,
  isAnabledInput = true,
  inputName,
  inputValue,
  inputType = "text",
  inputPlaceholder,
  inputError,
  onInputChange,
  onInputBlur,
  calculatedPrice = null,
  inputProps = {},
  tooltip,
  badge,
  isIncluded = false,
}) => {
  const helperTextId = useId();
  const helperText = tooltip || description;
  const hasInput = !!isAnabledInput;
  const displayPrice = isIncluded ? price : isSelected && calculatedPrice ? calculatedPrice : price;

  const handleClick = () => {
    if (!isIncluded && !isDesabled && onSelect) {
      onSelect();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  const cardClasses = twMerge(
    "flex min-h-[72px] flex-col justify-center gap-2 rounded-xl border bg-white px-3 py-3 transition duration-200",
    "shadow-[0_10px_26px_rgba(15,23,42,0.04)]",
    isIncluded
      ? "cursor-default border-[#0020BD] bg-[#f5f8ff] shadow-[0_14px_32px_rgba(0,32,189,0.10)]"
      : isDesabled
      ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
      : isSelected
      ? "cursor-pointer border-[#0020BD] bg-[#f5f8ff] shadow-[0_14px_32px_rgba(0,32,189,0.10)]"
      : "cursor-pointer border-slate-200 hover:border-[#9db1ff] hover:bg-[#f8faff]"
  );

  const iconWrapperClasses = twMerge(
    "hidden h-8 w-8 shrink-0 items-center justify-center rounded-md xl:flex",
    isDesabled
      ? "bg-slate-300"
      : isSelected || isIncluded
      ? "bg-[#0020BD]"
      : "bg-[#eef3ff] text-[#0020BD]"
  );

  const titleClasses = twMerge(
    "text-[13px] font-semibold leading-[18px] sm:text-[13.5px]",
    isDesabled ? "text-slate-500" : isSelected || isIncluded ? "text-[#172554]" : "text-[#2F3464]"
  );

  const priceClasses = twMerge(
    "text-right text-[12px] font-semibold leading-4 sm:text-[12.5px]",
    isDesabled ? "text-slate-400" : isSelected || isIncluded ? "text-[#0020BD]" : "text-slate-500"
  );

  const checkClasses = twMerge(
    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition duration-200",
    isDesabled
      ? "border-slate-300 bg-slate-100 text-transparent"
      : isSelected || isIncluded
      ? "border-[#0020BD] bg-[#0020BD] text-white"
      : "border-slate-300 bg-white text-transparent"
  );

  const tooltipClasses = "additional-service-tooltip pointer-events-none absolute z-30 w-[min(320px,calc(100vw-48px))] rounded-lg bg-slate-950 px-3 py-2 text-center text-xs font-medium leading-5 text-white shadow-xl";

  return (
    <div
      className="additional-service-card relative min-h-[72px]"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isIncluded ? "group" : "button"}
      tabIndex={isIncluded || isDesabled ? -1 : 0}
      aria-pressed={isIncluded ? undefined : isSelected}
      aria-disabled={isIncluded || isDesabled}
      aria-label={isIncluded ? `${title}, included` : undefined}
      aria-describedby={helperText ? helperTextId : undefined}
    >
      {badge && (
        <span className="absolute -top-2 right-3 z-10 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase leading-4 text-white shadow-sm">
          {badge}
        </span>
      )}
      {helperText && !hasInput && (
        <div id={helperTextId} className={`${tooltipClasses} left-1/2 top-full mt-2 -translate-x-1/2`}>
          {helperText}
        </div>
      )}
      <div 
        className={cardClasses}
        // style={true ? { boxShadow: '1px 2px 8px 1px #0020BD1F' } : {}}
        >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
            <div className={checkClasses}>
              <Check size={13} strokeWidth={3} />
            </div>
            <div className={iconWrapperClasses}>
              <Icon icon={icon || ""} width={18} height={18} color={isSelected || isIncluded ? "#ffffff" : "#0020BD"} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <div className={titleClasses}>{title}</div>
              </div>
              {hasInput && helperText && (
                <div className="additional-service-info relative mt-1">
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-[#9db1ff] hover:text-[#0020BD]"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label={`${title} details`}
                  >
                    <Info size={14} />
                  </button>
                  <div id={helperTextId} className="additional-service-info-tooltip additional-service-tooltip pointer-events-none absolute left-0 top-full z-30 mt-2 w-[min(320px,calc(100vw-48px))] rounded-lg bg-slate-950 px-3 py-2 text-center text-xs font-medium leading-5 text-white shadow-xl">
                    {helperText}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex max-w-[78px] shrink-0 flex-col items-end gap-1 sm:max-w-[86px]">
            <div className={priceClasses}>{displayPrice}</div>
            {isSelected && !isIncluded && !isDesabled && price === "Free" && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                Included
              </span>
            )}
          </div>
        </div>
        {hasInput && isSelected && !isIncluded && !isDesabled && (
          <div className="mt-1 rounded-lg border border-[#dfe7ff] bg-white px-3 py-3">
            <input
              value={inputValue}
              name={inputName}
              onChange={(e) => onInputChange(inputName, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => onInputBlur(inputName, e.target.value)}
              placeholder={inputPlaceholder}
              type={inputType}
              className="block h-10 w-full rounded-md border border-slate-200 px-3 py-2 font-inter text-[14px] text-secondary outline-none transition focus:border-[#0020BD] focus:ring-2 focus:ring-[#dbe5ff]"
              {...inputProps} 
            />
            {inputError && (
              <p className="mt-1 text-xs font-medium text-red-400">
                {inputError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>

   
  );
};

export default AditionalServiceCard;
